// src/pages/OnusOLT.jsx
import React, { useEffect, useState } from "react";
import "./OnusOLT.css";
import Toast from "../components/Toast";
import { authFetch } from "../utils/authFetch";
import { useTranscurrido } from "../utils/useTranscurrido";
import {
  Boton, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";

export default function OnusOLT() {
  const [modo, setModo] = useState("pon"); // "pon" | "slot" | "potencia"

  // La consulta de potencia no devuelve ONUs para guardar, sino un
  // diagnóstico del enlace. Va en su propio estado para no mezclarse
  // con `onus`, que es lo que alimenta "Guardar DB".
  const [potencias, setPotencias] = useState([]);


  // Selección OLT
  const [frame, setFrame] = useState(0);
  const [slot, setSlot] = useState(0); // board 0..7

  // Modo PON (0..15)
  const [pon, setPon] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onus, setOnus] = useState([]);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Toast
  const [toast, setToast] = useState(null); // { mensaje, tipo }

  // Qué nodo se está importando. Estaba fijo en 1 en cuatro llamadas:
  // con dos OLTs, importar del nodo nuevo habría leído el viejo.
  const [olts, setOlts] = useState([]);
  const [oltId, setOltId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch("/api/olt-config/");
        const data = await res.json();
        const lista = Array.isArray(data.olts) ? data.olts : [];
        setOlts(lista);
        if (lista.length > 0) setOltId(lista[0].id);
      } catch (e) {
        console.log("Error cargando OLTs:", e);
      }
    })();
  }, []);

  async function guardarEnDB() {
    setError("");
    setSaving(true);

    try {
      const payload = {
        olt_id: oltId,
        onus: onus.filter(
          (x) => x.cliente_id !== null && x.cliente_id !== undefined
        ),
      };

      const res = await authFetch("/api/olt/onus/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.status !== "OK") {
        setError(data.detalle || "No se pudo guardar en la BD");
        return;
      }

      setToast({
        tipo: "success",
        mensaje: `Guardado OK: ${data.guardadas}/${data.intentadas_guardar}`,
      });
    } catch (e) {
      setError("Error de red guardando en la BD");
    } finally {
      setSaving(false);
    }
  }

  async function actualizarEstadoEnDB() {
    setError("");
    setUpdating(true);

    try {
      const payload = {
        olt_id: oltId,
        estados: onus
          .filter((x) => x?.sn) // requiere serial
          .map((x) => ({
            serial: x.sn,
            // elige uno:
            estado: x.run_state === "online" ? "activo" : "inactivo",
            // estado: x.run_state === "online" ? "online" : "offline",
          })),
      };

      const res = await authFetch("/api/olt/olt/onus/update_estado", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.status !== "OK") {
        setError(data.detalle || "No se pudo actualizar estado en la BD");
        setUpdating(false);
        return;
      }

      setToast({
        tipo: "success",
        mensaje: `Estado actualizado: ${data.actualizadas}/${data.intentadas} (no encontradas: ${data.no_encontradas})`,
      });

      setUpdating(false);
    } catch (e) {
      setError("Error de red actualizando estado en la BD");
      setUpdating(false);
    }
  }

  async function consultar() {
    setError("");
    setLoading(true);
    setOnus([]);
    setPotencias([]);

    try {
      let url = "";

      if (modo === "potencia") {
        const res = await authFetch(
          `/api/olt/pon/${frame}/${slot}/${pon}/potencia?olt_id=${oltId}`
        );
        const data = await res.json();
        if (!res.ok || data.status !== "OK") {
          setError(data.detalle || "No se pudo consultar la potencia");
          setPotencias([]);
          return;
        }
        setPotencias(data.onus || []);
        return;
      }

      if (modo === "pon") {
        // ✅ ya existe
        url = `/api/olt/pon/${frame}/${slot}/${pon}/summary_min?olt_id=${oltId}`;
      } else {
        // ✅ nuevo endpoint: consulta TODO el board (slot) en una sola llamada
        // backend ejecuta:
        // - display ont info summary {frame}/{slot}
        // - display service-port board {frame}/{slot}
        url = `/api/olt/board/${frame}/${slot}/summary_min?olt_id=${oltId}`;
      }

      const res = await authFetch(url);
      const data = await res.json();

      if (!res.ok || data.status !== "OK") {
        setError(data.detalle || "No se pudo consultar la OLT");
        return;
      }

      const list = Array.isArray(data.onus) ? data.onus : [];

      // ✅ orden útil en modo slot: por PON y luego ont_id
      const sorted = [...list].sort((a, b) => {
        const pa = _ponOf(a.fsp || a.puerto);
        const pb = _ponOf(b.fsp || b.puerto);
        if (pa !== pb) return pa - pb;
        return Number(a.ont_id || 0) - Number(b.ont_id || 0);
      });

      setOnus(sorted);
    } catch (e) {
      setError("Error de red consultando la OLT");
    } finally {
      setLoading(false);
    }
  }

  const titulo =
    modo === "pon"
      ? `PON ${frame}/${slot}/${pon}`
      : modo === "potencia"
      ? `Potencia óptica · PON ${frame}/${slot}/${pon}`
      : `SLOT/BOARD ${frame}/${slot} (PON 0–15)`;

  return (
    <div className="onus-page">

      <EncabezadoPagina
        icono={Iconos.importar}
        titulo="Importar ONUs desde la OLT"
        resumen="Consulta por PON o por SLOT (board) y guarda el resultado."
        acciones={
          <>
            {olts.length > 1 && (
              <select
                className="onus-selector-olt"
                value={oltId ?? ""}
                onChange={(e) => {
                  setOltId(Number(e.target.value));
                  setOnus([]);
                }}
              >
                {olts.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nombre || o.host}
                  </option>
                ))}
              </select>
            )}
            <Boton
              variante="secundario"
              icono={Iconos.descargar}
              cargando={saving}
              disabled={loading || onus.length === 0}
              onClick={guardarEnDB}
            >
              Guardar DB
            </Boton>
            <Boton
              variante="secundario"
              icono={Iconos.actualizar}
              cargando={updating}
              disabled={saving || loading || onus.length === 0}
              onClick={actualizarEstadoEnDB}
            >
              Actualizar DB
            </Boton>
          </>
        }
      />

      <div className="onus-header">
        <div className="onus-segment">
          <button
            className={`seg-btn ${modo === "pon" ? "active" : ""}`}
            onClick={() => setModo("pon")}
            type="button"
            disabled={loading || saving}
          >
            Consultar PON
          </button>

          <button
            className={`seg-btn ${modo === "slot" ? "active" : ""}`}
            onClick={() => setModo("slot")}
            type="button"
            disabled={loading || saving}
          >
            Consultar SLOT
          </button>

          <button
            className={`seg-btn ${modo === "potencia" ? "active" : ""}`}
            onClick={() => setModo("potencia")}
            type="button"
            disabled={loading || saving}
          >
            Consultar Potencia
          </button>
        </div>
      </div>

      <div className="onus-toolbar">
        <div className="onus-card">
          <label>Frame</label>
          <input
            type="number"
            value={frame}
            onChange={(e) => setFrame(Number(e.target.value))}
            disabled={loading || saving}
          />
        </div>

        <div className="onus-card">
          <label>Slot (Board)</label>
          <select
            value={slot}
            onChange={(e) => setSlot(Number(e.target.value))}
            disabled={loading || saving}
          >
            {Array.from({ length: 8 }, (_, i) => i).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {(modo === "pon" || modo === "potencia") && (
          <div className="onus-card">
            <label>PON</label>
            <select
              value={pon}
              onChange={(e) => setPon(Number(e.target.value))}
              disabled={loading || saving}
            >
              {Array.from({ length: 16 }, (_, i) => i).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        )}

        <Boton
          icono={Iconos.buscar}
          cargando={loading}
          disabled={saving || !oltId}
          onClick={consultar}
        >
          Consultar
        </Boton>
      </div>

      {error && <div className="onus-error">{error}</div>}

      {/* ✅ SIEMPRE UNA SOLA TABLA */}
      <div className="onus-results">
        {loading ? (
          <PonCargando modo={modo} frame={frame} slot={slot} pon={pon} />
        ) : modo === "potencia" ? (
          <TablaPotencia
            titulo={titulo}
            rows={potencias}
            frame={frame}
            slot={slot}
            pon={pon}
          />
        ) : (
          <OnusTable titulo={titulo} rows={onus} />
        )}
      </div>

      {toast && (
        <Toast
          mensaje={toast.mensaje}
          tipo={toast.tipo}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

function _ponOf(fsp) {
  try {
    const s = String(fsp || "");
    const last = s.split("/").pop();
    return Number(last || 0);
  } catch {
    return 0;
  }
}

/* ======================================================
   TABLA DE POTENCIA ÓPTICA

   Es una pantalla de diagnóstico, no de inventario: por eso
   no comparte tabla con las ONUs ni tiene "Guardar DB". Lo
   que se busca aquí es encontrar rápido la ONT que está mal.
   ====================================================== */
/* Un dato que no existe se escribe como que no existe.

   Una lectura de la OLT puede traer huecos —una ONT que no contestó, un
   campo que el firmware no rellena— y `null.toFixed()` deja la pantalla
   entera en blanco: la excepción desmonta el árbol de React. Pasó. */
const cifra = (v, dec = 2, sufijo = "") =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "—"
    : `${Number(v).toFixed(dec)}${sufijo}`;

const km = (metros) =>
  metros === null || metros === undefined ? "—" : `${(metros / 1000).toFixed(2)} km`;

function TablaPotencia({ titulo, rows, frame, slot, pon }) {
  if (!rows.length) {
    return (
      <div className="onus-vacio">
        Elige el slot y el PON y pulsa Consultar. La OLT interroga a cada
        ONT por aire, así que un PON lleno tarda.
      </div>
    );
  }

  // Por número de ONT, ascendente: así la tabla se corresponde con el
  // orden en que la OLT las lista y se encuentra una ONT concreta de un
  // vistazo. El tinte de la fila es lo que destaca las que están mal.
  const ordenadas = [...rows].sort((a, b) => a.ont_id - b.ont_id);
  const cuenta = (e) => rows.filter((r) => r.estado === e).length;

  return (
    <div className="onus-tabla-card">
      <div className="onus-tabla-head">
        <h3>{titulo}</h3>
        <div className="pot-resumen">
          <Insignia variante="activo" texto={`${cuenta("buena")} buenas`} />
          {cuenta("regular") > 0 && (
            <Insignia variante="suspendido" texto={`${cuenta("regular")} al límite`} />
          )}
          {cuenta("mala") > 0 && (
            <Insignia variante="cortado" texto={`${cuenta("mala")} malas`} />
          )}
          {cuenta("saturada") > 0 && (
            <Insignia variante="cortado" texto={`${cuenta("saturada")} saturadas`} />
          )}
          {rows.filter((r) => !r.cliente_id).length > 0 && (
            <Insignia
              variante="neutra"
              texto={`${rows.filter((r) => !r.cliente_id).length} sin registrar`}
            />
          )}
        </div>
      </div>

      {/* El mismo mapa que giraba mientras se consultaba, ahora quieto y
          pintado: de un vistazo se ve si el PON está sano o si hay un
          racimo de ONTs malas juntas — que suele ser un problema del
          divisor, no de los clientes. */}
      <DispersionPon rows={rows} />

      <div className="pot-scroll">
        <table className="pot-tabla">
          <thead>
            <tr>
              <th>ID Cliente</th>
              <th>ONT</th>
              <th>Rx (dBm)</th>
              <th>Tx (dBm)</th>
              <th>La OLT la recibe</th>
              <th>Temp</th>
              <th>Distancia</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((r) => (
              <tr key={r.ont_id} className={`pot-${r.estado}`}>
                {/* Una ONT sin cliente en la base está autorizada en la OLT
                    pero no registrada aquí: conviene que se note. */}
                <td className={r.cliente_id ? "pot-cliente" : "pot-sin-cliente"}>
                  {r.cliente_id ?? "sin registrar"}
                </td>
                <td>{r.ont_id}</td>
                <td className="pot-rx">{cifra(r.rx)}</td>
                <td>{cifra(r.tx)}</td>
                <td>{cifra(r.olt_rx)}</td>
                <td>{cifra(r.temperatura, 0, " °C")}</td>
                <td>{km(r.distancia)}</td>
                <td>
                  <Insignia
                    variante={
                      r.estado === "buena"
                        ? "activo"
                        : r.estado === "regular"
                        ? "suspendido"
                        : "cortado"
                    }
                    texto={r.estado}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="pot-nota">
        Referencia GPON clase B+: por encima de −25 dBm está bien, hasta
        −28 aún cumple pero sin margen, y por debajo el enlace falla. Más
        de −8 dBm es demasiada luz y también lo rompe.
      </p>
    </div>
  );
}

/* ======================================================
   LA FIBRA

   Dejé de dibujar diagramas del PON para la espera. Lo que
   está pasando ahí abajo es luz saliendo de la OLT, llegando
   al divisor y repartiéndose a las casas — y eso es lo único
   que se muestra: un pulso recorriendo el hilo, una y otra
   vez, mientras la OLT contesta.

   No afirma nada sobre ninguna ONT. No hay datos que fingir
   ni cuadros que interpretar. Solo el gesto de la red
   trabajando, que es lo que de verdad ocurre.
   ====================================================== */
const RAMAS_Y = [12, 40, 68, 96, 124];

function FibraViva() {
  const salida = { x: 196, y: 68 };
  const borde = 404;

  return (
    <svg viewBox="0 0 420 136" className="fibra" aria-hidden="true">
      {/* La OLT */}
      <rect x="8" y="52" width="46" height="32" rx="6" className="fibra__olt" />
      <text x="31" y="72" className="fibra__olt-txt">OLT</text>

      {/* El tronco */}
      <path d="M 54 68 H 180" className="fibra__hilo" pathLength="100" />
      <path d="M 54 68 H 180" className="fibra__luz fibra__luz--tronco" pathLength="100" />

      {/* El divisor, y su destello cuando le llega el pulso */}
      <rect x="180" y="56" width="16" height="24" rx="3" className="fibra__divisor" />
      <rect x="180" y="56" width="16" height="24" rx="3" className="fibra__chispa" />

      {/* Las ramas hacia las casas */}
      {RAMAS_Y.map((y, i) => {
        const d = `M ${salida.x} ${salida.y} C 260 ${salida.y}, 300 ${y}, ${borde} ${y}`;
        return (
          <g key={y}>
            <path d={d} className="fibra__hilo" pathLength="100" />
            <path
              d={d}
              className="fibra__luz fibra__luz--rama"
              pathLength="100"
              style={{ animationDelay: `${i * 55}ms` }}
            />
            {/* La casa al final del hilo. Se enciende cuando el pulso
                llega: sin esto las ramas se cortaban en el vacío y el
                viaje no terminaba en ningún lado. */}
            <circle cx={borde} cy={y} r="4.5" className="fibra__ont" />
            <circle
              cx={borde}
              cy={y}
              r="4.5"
              className="fibra__ont-viva"
              style={{ animationDelay: `${i * 55}ms` }}
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ======================================================
   LA DISPERSIÓN

   Los dos ejes son medidas reales: a lo largo, los metros de
   fibra que la OLT reporta; a lo alto, los dBm que le llegan
   a cada ONT. Un punto por ONT, sin nada inventado.

   La forma es el diagnóstico:
     · una nube que baja despacio hacia la derecha -> normal,
       cuanto más lejos, menos luz
     · un punto MUY por debajo de la nube -> ése está roto,
       da igual a qué distancia esté
     · la nube entera hundida -> ni es de nadie ni es la
       distancia: es el tronco o el divisor

   El eje vertical se ajusta a los datos pero SIEMPRE deja
   dentro −25 y −28. Sin eso, en un PON sano las dos rayas se
   salían del cuadro y no había contra qué comparar.
   ====================================================== */
/* Deliberadamente pequeña. La gráfica sirve para ver de un vistazo si el
   PON tiene forma sana y dónde está el bicho raro; lo que se trabaja es la
   tabla de abajo. Si ocupa media pantalla, se roba la atención de lo que
   de verdad se usa. */
const D_ANCHO = 420, D_ALTO = 124;
const D_IZQ = 30, D_DER = 410, D_ARR = 10, D_ABA = 96;

function DispersionPon({ rows }) {
  const datos = (rows || []).filter(
    (r) => typeof r.rx === "number" && !Number.isNaN(r.rx)
  );
  if (!datos.length) return null;

  const masLejos = datos.reduce((m, r) => Math.max(m, r.distancia || 0), 0);
  const topeKm = Math.max(2, Math.ceil((masLejos || 2000) / 1000));

  // El alto se ajusta a lo que hay, con un margen, pero sin dejar fuera
  // los umbrales: son la referencia contra la que se mira todo.
  const rxs = datos.map((r) => r.rx);
  const techo = Math.ceil(Math.max(-22, ...rxs) + 1);
  const suelo = Math.floor(Math.min(-29, ...rxs) - 1);

  const ejeX = (m) => D_IZQ + (m / (topeKm * 1000)) * (D_DER - D_IZQ);
  const ejeY = (dbm) => {
    const v = Math.max(suelo, Math.min(techo, dbm));
    return D_ABA - ((v - suelo) / (techo - suelo)) * (D_ABA - D_ARR);
  };

  const kms = Array.from({ length: topeKm + 1 }, (_, i) => i)
    .filter((k) => topeKm <= 6 || k % 2 === 0);

  // Marcas de dBm cada 2, dentro del rango que se está mostrando.
  const dbms = [];
  for (let v = Math.ceil(suelo / 3) * 3; v <= techo; v += 3) dbms.push(v);

  return (
    <div className="disp">
      <svg viewBox={`0 0 ${D_ANCHO} ${D_ALTO}`} className="disp__lienzo">
        {/* La franja donde el enlace ya no aguanta */}
        <rect x={D_IZQ} y={ejeY(-28)} width={D_DER - D_IZQ}
              height={D_ABA - ejeY(-28)} className="disp__zona" />

        {dbms.map((v) => (
          <g key={v}>
            <line x1={D_IZQ} y1={ejeY(v)} x2={D_DER} y2={ejeY(v)} className="disp__reja" />
            <text x={D_IZQ - 5} y={ejeY(v) + 3} className="disp__rot disp__rot--y">{v}</text>
          </g>
        ))}

        {kms.map((k) => (
          <g key={k}>
            <line x1={ejeX(k * 1000)} y1={D_ARR} x2={ejeX(k * 1000)} y2={D_ABA}
                  className="disp__reja" />
            <text x={ejeX(k * 1000)} y={D_ABA + 11} className="disp__rot">{k}</text>
          </g>
        ))}

        {/* Los dos umbrales, marcados por encima de la rejilla */}
        <line x1={D_IZQ} y1={ejeY(-25)} x2={D_DER} y2={ejeY(-25)} className="disp__umbral" />
        <line x1={D_IZQ} y1={ejeY(-28)} x2={D_DER} y2={ejeY(-28)}
              className="disp__umbral disp__umbral--duro" />

        {/* Ejes y la OLT en el kilómetro cero */}
        <line x1={D_IZQ} y1={D_ARR} x2={D_IZQ} y2={D_ABA} className="disp__eje" />
        <line x1={D_IZQ} y1={D_ABA} x2={D_DER} y2={D_ABA} className="disp__eje" />
        <text x={D_IZQ} y={D_ABA + 22} className="disp__olt-txt">OLT · km</text>

        {/* Un punto por ONT. Semitransparentes: donde se solapan, la nube
            se ve más densa en vez de perderse unas debajo de otras. */}
        {[...datos]
          .sort((a, b) => (a.distancia || 0) - (b.distancia || 0))
          .map((r, i) => (
            <circle
              key={r.ont_id}
              cx={ejeX(r.distancia || 0)}
              cy={ejeY(r.rx)}
              r={r.estado === "buena" ? 2.3 : 3.4}
              className={`disp__ont disp__ont--${r.estado}`}
              style={{ animationDelay: `${i * 14}ms` }}
            >
              <title>
                {`ONT ${r.ont_id}${r.cliente_id ? ` · cliente ${r.cliente_id}` : ""}\n` +
                 `${r.rx} dBm · ${((r.distancia || 0) / 1000).toFixed(2)} km`}
              </title>
            </circle>
          ))}
      </svg>

    </div>
  );
}

/* La espera, en el sitio donde va a salir el resultado. No tapa la
   pantalla: es una lectura, no una operación que haya que vigilar. Y al
   ocupar el mismo hueco que la tabla, cuando llega la respuesta no hay
   salto — una cosa se convierte en la otra. */
function PonCargando({ modo, frame, slot, pon }) {
  const seg = useTranscurrido(true);
  const mm = String(Math.floor(seg / 60)).padStart(2, "0");
  const ss = String(seg % 60).padStart(2, "0");

  const donde = modo === "slot" ? `${frame}/${slot}` : `${frame}/${slot}/${pon}`;
  const que =
    modo === "potencia"
      ? "Midiendo la potencia de cada ONT"
      : modo === "slot"
      ? "Leyendo los dieciséis PON del board"
      : "Leyendo las ONTs del PON";

  return (
    <div className="onus-tabla-card pon-cargando" role="status" aria-live="polite">
      <div className="onus-tabla-head">
        <h3>
          {que} <code className="pon-donde">{donde}</code>
        </h3>
        <span className="pon-reloj">{mm}:{ss}</span>
      </div>

      {/* La misma espera para los tres modos: se está hablando con el
          mismo equipo por el mismo cable, así que no hay razón para que
          se vea distinto según lo que se le pregunte. */}
      <FibraViva />

      <p className="pot-nota">
        {modo === "potencia"
          ? "La OLT pregunta a cada ONT por aire, una por una."
          : "Una sola sesión SSH contra la OLT."}
        {/* Medido contra la X7: un PON de 63 ONTs tarda unos 9 segundos.
            El aviso va a los 30 — pasado eso ya no es lentitud normal. */}
        {seg > 30 && (
          <>
            {" "}
            <b>Lleva más de treinta segundos</b>: lo normal son unos diez. Si
            pasa del minuto, algo se atoró.
          </>
        )}
      </p>
    </div>
  );
}

function LoadingOverlay({ texto = "Cargando..." }) {
  return (
    <div className="onus-loading-overlay" role="status" aria-live="polite">
      <div className="onus-loading-card">
        <div className="onus-spinner" />
        <div className="onus-loading-text">{texto}</div>
      </div>
    </div>
  );
}

function OnusTable({ titulo, rows }) {
  return (
    <div className="onus-table-card">
      <div className="onus-table-head">
        <h2>
          <Iconos.onus size={17} strokeWidth={1.8} />
          {titulo}
        </h2>
        <Insignia variante="neutra" texto={`${rows.length} ONUs`} />
      </div>

      <div className="onus-table-wrap">
        <table className="onus-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>F/S/P</th>
              <th>ONT ID</th>
              <th>Service Port</th>
              <th>No. Serie</th>
              <th>Modelo</th>
              <th>Estado</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="onus-empty">
                  Sin ONUs
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={`${r.sn || "sn"}-${r.ont_id ?? idx}-${idx}`}>
                  <td>{r.cliente_id ?? "-"}</td>
                  <td>{r.fsp ?? r.puerto ?? "-"}</td>
                  <td>{r.ont_id ?? "-"}</td>
                  <td>{r.service_port ?? "-"}</td>
                  <td>{r.sn ?? "-"}</td>
                  <td>{r.model ?? "N/D"}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.run_state === "online" ? "ok" : "bad"
                      }`}
                    >
                      {r.run_state === "online" ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
